/**
 * Custom hook encapsulating the shared date-range filter state that is common
 * across GHGOverviewPage, CompanyGHGPage, StatisticsOverviewPage, and
 * CompanyStatisticsPage.
 *
 * Returns the state values and their setters so pages can use them directly
 * without duplicating identical useState declarations.
 */

import { useState } from 'react';

export interface DateRangeFilterState {
  filterMode: 'year' | 'dateRange';
  setFilterMode: (mode: 'year' | 'dateRange') => void;
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  fromDate: Date | null;
  setFromDate: (date: Date | null) => void;
  toDate: Date | null;
  setToDate: (date: Date | null) => void;
  availableYears: number[];
  setAvailableYears: (years: number[]) => void;
  exporting: boolean;
  setExporting: (exporting: boolean) => void;
}

/**
 * Initialises the standard date-range filter state shared across GHG and
 * Statistics pages.  Call this once at the top of the component and spread /
 * destructure the returned object.
 */
export function useDateRangeFilter(): DateRangeFilterState {
  const currentYear = new Date().getFullYear();

  const [filterMode, setFilterMode] = useState<'year' | 'dateRange'>('year');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [fromDate, setFromDate] = useState<Date | null>(new Date(currentYear, 0, 1));
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);

  return {
    filterMode,
    setFilterMode,
    selectedYear,
    setSelectedYear,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    availableYears,
    setAvailableYears,
    exporting,
    setExporting,
  };
}
